import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { PositionModel, PositionType, Side, OptionType, GetCustomerDataResponse, GetCustomerSettingsResponse, LocationModel } from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import { 
    MtmReportRow, 
    parseCustomData, 
    getToolDescription, 
    calculatePositionValue, 
    calculateMarketValue, 
    calculateMtmValue,
    calculateReportSummary,
    ReportSummary
} from './Types';

const MMBTU_PER_CONTRACT = 10000;

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

interface MtmReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
    defaultBudgetStartDate?: Date;
    defaultBudgetEndDate?: Date;
}

const MtmReport: React.FC<MtmReportProps> = ({
    customerData,
    customerSettings,
    defaultQuoteDate,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());

    // Default start date = first of next month, end date = one year from today (match HedgingPositionReport)
    const [startMonth, setStartMonth] = useState<number>(dayjs().add(1, 'month').month() + 1);
    const [startYear, setStartYear] = useState<number>(dayjs().add(1, 'month').year());
    const [endMonth, setEndMonth] = useState<number>(dayjs().add(1, 'year').month() + 1);
    const [endYear, setEndYear] = useState<number>(dayjs().add(1, 'year').year());

    const locations: LocationModel[] = customerData?.locations || [];
    const [selectedLocation, setSelectedLocation] = useState<number | undefined>(undefined);
    const [hasRun, setHasRun] = useState<boolean>(false);
    const [displayFees, setDisplayFees] = useState<boolean>(false);

    const selectedLocationName = locations.find(l => l.locationId === selectedLocation)?.locationName || '';

    // Default to first location once customer data loads
    useEffect(() => {
        if (selectedLocation === undefined && locations.length > 0 && locations[0].locationId !== undefined) {
            setSelectedLocation(locations[0].locationId);
        }
    }, [locations, selectedLocation]);

    const fetchPositions = async () => {
        const response = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            true, // includeExpirationDates
            true,  // includeMarketPrices
            true,  // useDisplayUnits
            undefined, // scenarioMarketPrices
            undefined, // commodityFilter
            selectedLocation ? [selectedLocation] : undefined,
            undefined, // counterpartyFilter
            undefined, // tradeStartDate
            undefined, // tradeEndDate
            startYear,
            startMonth,
            undefined, // budgetLabel
            endYear,
            endMonth,
            undefined, // budgetMonthEndFilterLabel
            undefined, // contractMonthStartFilterYear
            undefined, // contractMonthStartFilterMonth
            undefined, // contractMonthStartFilterLabel
            undefined, // contractMonthEndFilterYear
            undefined, // contractMonthEndFilterMonth
            undefined, // contractMonthEndFilterLabel
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            undefined, // includeAllocations
            undefined, // includeAllocationOffset
            true       // filterByTradeDate - report runs as of the quote date
        );

        setPositions(response.data.rows || []);
        setHasRun(true);
    };

    const handleSubmit = () => {
        fetchPositions();
    };

    // Identify offsetting Buy+Sell pairs within the same tenure, strike,
    // optionType, positionType, and commodity. Sells contribute a negative
    // quantity, buys a positive one, so a Buy of +X is offset by a Sell of -X.
    // Each pair is kept as an offsetGroup and rendered as a single aggregated
    // row (instead of being removed entirely).
    const { openPositions, offsetGroups } = useMemo<{
        openPositions: PositionModel[];
        offsetGroups: PositionModel[][];
    }>(() => {
        const open = positions.filter(p => !p.isExpired);

        const baseKey = (p: PositionModel) => [
            p.budgetDate?.year ?? '',
            p.budgetDate?.month ?? '',
            p.optionStrike ?? '',
            p.optionType ?? '',
            p.positionType ?? '',
            p.commodity?.commodityId ?? '',
        ].join('|');

        const signedQuantity = (p: PositionModel) =>
            (p.volume ?? 0) * (p.side === Side.Buy ? 1 : -1);

        const excluded = new Set<PositionModel>();
        const groups: PositionModel[][] = [];
        const sellsByKey = new Map<string, PositionModel[]>();
        open.forEach(p => {
            if (p.side === Side.Sell) {
                const key = `${baseKey(p)}|${signedQuantity(p)}`;
                const list = sellsByKey.get(key) ?? [];
                list.push(p);
                sellsByKey.set(key, list);
            }
        });
        open.forEach(p => {
            if (p.side !== Side.Buy) return;
            // Find a sell whose signed quantity is the inverse of this buy's.
            const lookupKey = `${baseKey(p)}|${-signedQuantity(p)}`;
            const list = sellsByKey.get(lookupKey);
            if (list && list.length > 0) {
                const match = list.shift()!;
                excluded.add(p);
                excluded.add(match);
                groups.push([p, match]);
            }
        });

        return {
            openPositions: open.filter(p => !excluded.has(p)),
            offsetGroups: groups,
        };
    }, [positions]);

    const reportRows: MtmReportRow[] = useMemo(() => {
        const buildStandaloneRow = (position: PositionModel): MtmReportRow => {
            const customData = parseCustomData(position.customData);
            const y = position.budgetDate.year ?? 0;
            const m = position.budgetDate.month ?? 1;
            const signedVolume = (position.volume ?? 0) * (position.side === Side.Buy ? 1 : -1);
            const price = position.price ?? 0;
            const marketPrice = position.marketPrice ?? 0;
            const purchaseCost = price * signedVolume;
            const marketValue = marketPrice * signedVolume;
            return {
                tenureDate: `${y}-${String(m).padStart(2, '0')}`,
                formattedDate: dayjs(`${y}-${m}-01`).format('MMM-YYYY'),
                tool: getToolDescription(position),
                counterpartyName: position.counterparty?.counterpartyName || '',
                tradeDate: position.tradeDate ? dayjs(position.tradeDate).format('MM/DD/YYYY') : '',
                quantity: signedVolume,
                strike: position.optionStrike ?? 0,
                price,
                purchaseCost,
                positionValue: calculatePositionValue(position),
                marketPrice,
                marketValue,
                mtmValue: calculateMtmValue(position),
                netVsMarket: marketValue - purchaseCost,
                commodity: position.commodity?.commodityName || '',
                dealTicketId: position.dealTicketNumber || position.dealTicketId?.toString() || '',
                fees: (customData.fees ?? customData.fee ?? 0) * (Math.abs(position.volume ?? 0) / MMBTU_PER_CONTRACT),
                expirationDate: position.expirationDate
                    ? dayjs(position.expirationDate).format('MM/DD/YYYY')
                        + (position.positionType === PositionType.Option ? ' E' : '')
                    : undefined,
                position,
            };
        };

        const buildOffsetRow = (group: PositionModel[]): MtmReportRow => {
            const first = group[0];
            const y = first.budgetDate?.year ?? 0;
            const m = first.budgetDate?.month ?? 1;

            // Pre-compute per-position values used for aggregation.
            const perPos = group.map(p => {
                const absVol = Math.abs(p.volume ?? 0);
                const sv = (p.volume ?? 0) * (p.side === Side.Buy ? 1 : -1);
                const price = p.price ?? 0;
                const marketPrice = p.marketPrice ?? 0;
                const purchaseCost = price * sv;
                const marketValue = marketPrice * sv;
                const customData = parseCustomData(p.customData);
                const feePerContract = customData.fees ?? customData.fee ?? 0;
                const fees = Math.abs(feePerContract * (absVol / MMBTU_PER_CONTRACT));
                return {
                    absVol,
                    sv,
                    price,
                    marketPrice,
                    purchaseCost,
                    marketValue,
                    netVsMarket: marketValue - purchaseCost,
                    fees,
                };
            });

            const totalAbsVolume = perPos.reduce((s, x) => s + x.absVol, 0);
            const wAvg = (selector: (x: typeof perPos[number]) => number) =>
                totalAbsVolume > 0
                    ? perPos.reduce((s, x) => s + selector(x) * x.absVol, 0) / totalAbsVolume
                    : 0;

            const netVolume = perPos.reduce((s, x) => s + x.sv, 0);
            const avgPrice = wAvg(x => x.price);
            const avgMarketPrice = wAvg(x => x.marketPrice);
            const avgPurchaseCost = wAvg(x => x.purchaseCost);
            const avgMarketValue = wAvg(x => x.marketValue);
            const avgNetVsMarket = wAvg(x => x.netVsMarket);
            const totalFees = perPos.reduce((s, x) => s + x.fees, 0);

            const allSameCp = group.every(p =>
                p.counterparty?.counterpartyId === first.counterparty?.counterpartyId);
            const counterpartyName = allSameCp
                ? (first.counterparty?.counterpartyName || '')
                : 'Multiple';

            const formatTd = (p: PositionModel) =>
                p.tradeDate ? dayjs(p.tradeDate).format('MM/DD/YYYY') : '';
            const firstTd = formatTd(first);
            const allSameTd = group.every(p => formatTd(p) === firstTd);
            const tradeDate = allSameTd ? firstTd : 'Multiple';

            const formatExp = (p: PositionModel) =>
                p.expirationDate ? dayjs(p.expirationDate).format('MM/DD/YYYY') : '';
            const firstExp = formatExp(first);
            const allSameExp = group.every(p => formatExp(p) === firstExp);
            const expSuffix = first.positionType === PositionType.Option ? ' E' : '';
            const expirationDate = allSameExp
                ? (firstExp ? firstExp + expSuffix : undefined)
                : 'Multiple' + expSuffix;

            return {
                tenureDate: `${y}-${String(m).padStart(2, '0')}`,
                formattedDate: dayjs(`${y}-${m}-01`).format('MMM-YYYY'),
                tool: getToolDescription(first),
                counterpartyName,
                tradeDate,
                quantity: netVolume,
                strike: first.optionStrike ?? 0,
                price: avgPrice,
                purchaseCost: avgPurchaseCost,
                positionValue: avgPurchaseCost,
                marketPrice: avgMarketPrice,
                marketValue: avgMarketValue,
                mtmValue: avgNetVsMarket,
                netVsMarket: avgNetVsMarket,
                commodity: first.commodity?.commodityName || '',
                dealTicketId: '',
                fees: totalFees,
                expirationDate,
                position: first,
            };
        };

        return [
            ...openPositions.map(buildStandaloneRow),
            ...offsetGroups.map(buildOffsetRow),
        ].sort((a, b) => {
            // Sort by tenure date, then trade date
            const dateCompare = a.tenureDate.localeCompare(b.tenureDate);
            if (dateCompare !== 0) return dateCompare;
            return a.tradeDate.localeCompare(b.tradeDate);
        });
    }, [openPositions, offsetGroups]);

    const summary: ReportSummary = useMemo(() => {
        // Include offset positions: the offset rows in the grid show weighted
        // averages, which don't sum meaningfully, so the summary derives true
        // totals straight from the underlying non-expired positions.
        const allNonExpired = positions.filter(p => !p.isExpired);
        const base = calculateReportSummary(allNonExpired);
        const totalMarketValue = allNonExpired.reduce((s, p) => {
            const sv = (p.volume ?? 0) * (p.side === Side.Buy ? 1 : -1);
            return s + (p.marketPrice ?? 0) * sv;
        }, 0);
        const totalMtmValue = allNonExpired.reduce((s, p) => {
            const sv = (p.volume ?? 0) * (p.side === Side.Buy ? 1 : -1);
            const purchaseCost = (p.price ?? 0) * sv;
            const marketValue = (p.marketPrice ?? 0) * sv;
            return s + (marketValue - purchaseCost);
        }, 0);
        const totalFees = allNonExpired.reduce((s, p) => {
            const customData = parseCustomData(p.customData);
            const feePerContract = customData.fees ?? customData.fee ?? 0;
            const contracts = Math.abs(p.volume ?? 0) / MMBTU_PER_CONTRACT;
            return s + Math.abs(feePerContract * contracts);
        }, 0);
        return { ...base, totalMarketValue, totalMtmValue, totalFees };
    }, [positions]);

    const columnDefs: ColDef<MtmReportRow>[] = useMemo(() => {
        const cols: ColDef<MtmReportRow>[] = [
            { field: 'formattedDate', headerName: 'Period', width: 100, pinned: 'left' },
            { field: 'tool', headerName: 'Tool', width: 100 },
            { field: 'counterpartyName', headerName: 'Counterparty', width: 150 },
            { field: 'tradeDate', headerName: 'Trade Date', width: 110 },
            { 
                field: 'quantity', 
                headerName: 'Quantity', 
                width: 100,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || ''
            },
            { 
                field: 'strike', 
                headerName: 'Strike', 
                width: 100,
                type: 'numericColumn',
                valueFormatter: params => params.value ? `$${params.value.toFixed(4)}` : ''
            },
            { 
                field: 'price', 
                headerName: 'Position Price', 
                width: 120,
                type: 'numericColumn',
                valueFormatter: params => params.value ? `$${params.value.toFixed(4)}` : ''
            },
            { 
                field: 'purchaseCost', 
                headerName: 'Purchase Cost/Proceeds', 
                width: 170,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            },
            { 
                field: 'expirationDate', 
                headerName: 'Expiration Date', 
                width: 130
            },
            { 
                field: 'marketPrice', 
                headerName: 'Market Price', 
                width: 110,
                type: 'numericColumn',
                valueFormatter: params => params.value ? `$${params.value.toFixed(4)}` : ''
            },
            { 
                field: 'marketValue', 
                headerName: 'Market Value', 
                width: 120,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            },
            { 
                field: 'netVsMarket', 
                headerName: 'Net Value vs. Current Market Value', 
                width: 220,
                type: 'numericColumn',
                cellStyle: params => ({
                    color: (params.value ?? 0) >= 0 ? 'green' : 'black'
                }),
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            },
        ];

        if (displayFees) {
            cols.push({
                field: 'fees',
                headerName: 'Fees',
                width: 110,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) || ''
            });
        }

        return cols;
    }, [displayFees]);

    const handleDownloadExcel = () => {
        if (reportRows.length === 0) return;

        const HEADERS = [
            'Period',
            'Tool',
            'Counterparty',
            'Trade Date',
            'Quantity',
            'Strike',
            'Position Price',
            'Purchase Cost/Proceeds',
            'Expiration Date',
            'Market Price',
            'Market Value',
            'Net Value vs. Current Market Value',
            ...(displayFees ? ['Fees'] : []),
        ];

        const title = `${selectedLocationName} Mark to Market Report`.trim();
        const subtitle = `Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`;

        const aoa: (string | number)[][] = [];
        aoa.push([title]);
        aoa.push([subtitle]);
        aoa.push(HEADERS);

        reportRows.forEach(r => {
            const row: (string | number)[] = [
                r.formattedDate,
                r.tool,
                r.counterpartyName,
                r.tradeDate,
                r.quantity,
                r.strike,
                r.price,
                r.purchaseCost,
                r.expirationDate ?? '',
                r.marketPrice,
                r.marketValue,
                r.netVsMarket,
            ];
            if (displayFees) row.push(r.fees);
            aoa.push(row);
        });

        // Totals row
        const totalsRow: (string | number)[] = [
            'Totals',
            '',
            '',
            '',
            summary.totalQuantity,
            '',
            '',
            summary.totalPositionValue,
            '',
            '',
            summary.totalMarketValue,
            summary.totalMtmValue,
        ];
        if (displayFees) totalsRow.push(summary.totalFees);
        aoa.push(totalsRow);

        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        const lastCol = HEADERS.length - 1;
        sheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        ];

        const currency2 = '"$"#,##0.00;[Black]("$"#,##0.00)';
        const currency4 = '"$"#,##0.0000;[Black]("$"#,##0.0000)';
        const currency0 = '"$"#,##0;[Black]("$"#,##0)';
        const intFmt = '#,##0';

        const dataStart = 3;
        for (let r = dataStart; r < aoa.length; r++) {
            const apply = (c: number, fmt: string) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[ref];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 'n';
                    cell.z = fmt;
                }
            };
            apply(4, intFmt);       // Quantity
            apply(5, currency4);    // Strike
            apply(6, currency4);    // Position Price
            apply(7, currency2);    // Purchase Cost/Proceeds
            // Column 8 is Expiration Date (text, no number format)
            apply(9, currency4);    // Market Price
            apply(10, currency0);   // Market Value
            apply(11, currency2);   // Net Value vs. Current Market Value
            if (displayFees) apply(12, currency2); // Fees
        }

        sheet['!cols'] = [
            { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 12 }, { wch: 12 },
            { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 30 },
            ...(displayFees ? [{ wch: 12 }] : []),
        ];

        const workbook = XLSX.utils.book_new();
        const sheetName = (selectedLocationName || 'MTM').replace(/[\\/*?:[\]]/g, '').substring(0, 31) || 'MTM';
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

        const filename = `HedgingMTMReport_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Mark to Market Report</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="Marking to Market Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={selectedLocation ?? ''}
                            label="Location"
                            onChange={(e) => setSelectedLocation(Number(e.target.value))}
                        >
                            {locations.map((l: LocationModel) => (
                                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={displayFees}
                                onChange={(e) => setDisplayFees(e.target.checked)}
                            />
                        }
                        label="Display Fees"
                    />
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || reportRows.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Save to Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <>
                    <StoneXGrid
                        columns={columnDefs}
                        rows={reportRows}
                        gridOptions={{
                            defaultColDef: {
                                headerClass: 'ag-centered-header',
                            },
                        }}
                    />

                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="h6">Summary</Typography>
                        <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                            <Typography>
                                <strong>Total Quantity:</strong> {summary.totalQuantity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </Typography>
                            <Typography>
                                <strong>Total Position Value:</strong> {summary.totalPositionValue.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                            </Typography>
                            <Typography>
                                <strong>Total Market Value:</strong> {summary.totalMarketValue.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                            </Typography>
                            <Typography>
                                <strong>Total MTM Value:</strong>
                                <span style={{ color: summary.totalMtmValue >= 0 ? 'green' : 'black' }}>
                                    {' '}{summary.totalMtmValue.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })}
                                </span>
                            </Typography>
                            {displayFees && (
                                <Typography>
                                    <strong>Total Fees:</strong> {summary.totalFees.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            )}

                        </Box>
                    </Paper>
                </>
            ) : null}
        </Box>
    );
};

export default MtmReport;
